import { useEffect, useRef, useState } from "react";
import type { SocketEvent } from "./types";

/** Re-renders the caller once a second. Used for the cooldown readout and the
    relative timestamps, which are the only things on screen that go stale on
    their own. */
export function useTick(intervalMs = 1000): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((n) => n + 1), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return tick;
}

export type SocketStatus = "connecting" | "live" | "reconnecting";

/** The panel socket: connect, reconnect on drop, keepalive so proxies don't
    time it out. The handler is held in a ref so a re-render never tears the
    connection down. */
export function useSocket(
  enabled: boolean,
  onEvent: (frame: SocketEvent) => void,
): SocketStatus {
  const [status, setStatus] = useState<SocketStatus>("connecting");
  const handler = useRef(onEvent);
  handler.current = onEvent;

  useEffect(() => {
    if (!enabled) return;
    let socket: WebSocket | null = null;
    let keepalive: ReturnType<typeof setInterval> | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let closed = false;

    const connect = () => {
      const scheme = location.protocol === "https:" ? "wss" : "ws";
      socket = new WebSocket(`${scheme}://${location.host}/ws`);

      socket.onopen = () => setStatus("live");
      socket.onclose = () => {
        if (closed) return;
        setStatus("reconnecting");
        retry = setTimeout(connect, 2000);
      };
      socket.onmessage = (message: MessageEvent<string>) => {
        try {
          handler.current(JSON.parse(message.data) as SocketEvent);
        } catch {
          /* a frame we can't parse is a frame we can't act on */
        }
      };

      clearInterval(keepalive);
      keepalive = setInterval(() => {
        if (socket?.readyState === WebSocket.OPEN) socket.send("ping");
      }, 25000);
    };

    connect();

    /* Coming back to a backgrounded tab.

       A phone suspends the page the moment you switch apps, and the socket dies
       with it — but `onclose` is delivered on resume, not while suspended, and
       iOS sometimes never delivers it at all. Either way the panel came back
       showing a green "live" dot over a socket that has been dead since the
       screen locked, which is the exact lie the connection banner exists to
       prevent. So on every return to the foreground, ask the socket what it
       actually is and reconnect at once rather than waiting out a retry timer
       that may never have been set. */
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const state = socket?.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) return;
      setStatus("reconnecting");
      clearTimeout(retry);
      connect();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      closed = true;
      clearInterval(keepalive);
      clearTimeout(retry);
      document.removeEventListener("visibilitychange", onVisible);
      socket?.close();
    };
  }, [enabled]);

  return status;
}

/** True while the element is the one holding focus. Both live sliders use it:
    another admin's change must never yank the thumb out from under a drag. */
export function useIsFocused(): [boolean, { onFocus: () => void; onBlur: () => void }] {
  const [focused, setFocused] = useState(false);
  return [focused, { onFocus: () => setFocused(true), onBlur: () => setFocused(false) }];
}

/** Live `matchMedia`. Reactive, so a rotation or a desktop window drag re-picks
    the layout; and a media query rather than the user agent, because the thing
    the panel actually needs to know is how much room and what kind of pointer
    it has, which a phone in a desktop-sized window does not have. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );

  useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = () => setMatches(list.matches);
    // The first paint used the value from render; re-read in case the query
    // changed between then and the effect running.
    onChange();
    list.addEventListener("change", onChange);
    return () => list.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/* Three questions, deliberately separate.

   `useIsMobile` is about room — the same 860px the stylesheet has always
   treated as the mobile boundary — and it decides which shell renders.
   `useCoarsePointer` is about the hand.

   `useCompactControls` is either one, and it is what the controls themselves
   ask: a popover anchored under a chip and a strip of six 28px icons are both
   wrong at 400px *and* wrong under a thumb, for different reasons — one has
   nowhere to open, the other is unhittable. A touch laptop is wide and coarse;
   a dragged-narrow desktop window is fine-pointed and cramped. Both want the
   sheet, so both get it. */
export const useIsMobile = () => useMediaQuery("(max-width: 860px)");
export const useCoarsePointer = () => useMediaQuery("(pointer: coarse)");
export function useCompactControls(): boolean {
  const narrow = useIsMobile();
  const touch = useCoarsePointer();
  return narrow || touch;
}
